/**
 * Edit Item Page with Image Upload
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import api from '../../config/axiosConfig';
import { fetchItemsAsync } from './inventorySlice';
import Loader from '../../components/common/Loader';

interface ItemData {
  id: number;
  sku: string;
  name: string;
  category: string;
  brand?: string;
  cost_price: number;
  sell_price: number;
  unit: string;
  description?: string;
  barcode?: string;
  has_warranty: boolean;
  min_stock: number;
  is_active: boolean;
  image?: {
    type: string;
    url?: string;
    has_custom: boolean;
  };
}

const EditItemPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<ItemData | null>(null);
  
  // Image upload states
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadMode, setImageUploadMode] = useState<'file' | 'url'>('file');
  const [imageUrl, setImageUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    loadItem();
    
    return () => {
      stopCamera();
    };
  }, [id]);

  const loadItem = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/items/${id}`);
      setItem(response.data);
      
      // Load existing image if any
      if (response.data.image?.url) {
        if (response.data.image.type === 'upload') {
          setImagePreview(`${api.defaults.baseURL}${response.data.image.url.substring(7)}`);
          setImageUploadMode('file');
        } else if (response.data.image.type === 'url') {
          setImagePreview(response.data.image.url);
          setImageUrl(response.data.image.url);
          setImageUploadMode('url');
        }
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      alert('Cannot access camera. Please allow camera permission or use file upload.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
            setImageFile(file);
            setImagePreview(canvas.toDataURL('image/jpeg'));
            stopCamera();
          }
        }, 'image/jpeg', 0.9);
      }
    }
  };

  const uploadImage = async () => {
    if (!imageFile || !item) return;
    
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', imageFile, imageFile.name);
      
      console.log('Uploading file:', imageFile.name, imageFile.type, imageFile.size);
      console.log('FormData entries:', Array.from(formData.entries()).map(([k, v]) => [k, v instanceof File ? `File: ${v.name}` : v]));
      
      // Important: Delete default Content-Type to let browser set multipart/form-data with boundary
      const response = await api.post(`/items/${item.id}/image`, formData, {
        headers: {
          'Content-Type': undefined, // Let browser set it automatically
        },
      });
      
      console.log('✅ Upload response:', response.data);
      alert('✅ Image uploaded successfully!');
      
      // Force reload from server (bypass cache)
      await loadItem();
      setImageFile(null); // Clear pending upload
    } catch (err: any) {
      console.error('❌ Upload error:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          console.error('Validation errors (array):', JSON.stringify(err.response.data.detail, null, 2));
          
          // Parse loc array to show field path
          const errorMessages = err.response.data.detail.map((e: any) => {
            const loc = e.loc ? e.loc.join('.') : 'unknown';
            return `[${loc}] ${e.msg || e.message || JSON.stringify(e)}`;
          });
          alert(`Failed to upload image:\n${errorMessages.join('\n')}`);
        } else {
          console.error('Error detail (not array):', err.response.data.detail);
          alert(`Failed to upload image: ${err.response.data.detail}`);
        }
      } else {
        alert(`Failed to upload image: ${err.message}`);
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const saveImageUrl = async () => {
    if (!imageUrl.trim()) {
      alert('Please enter a valid image URL');
      return;
    }

    try {
      setUploadingImage(true);
      const response = await api.put(`/items/${item.id}/image-url`, {
        url: imageUrl.trim()
      });
      
      console.log('✅ URL saved:', response.data);
      alert('✅ Image URL saved successfully!');
      
      // Reload item to get updated image info
      await loadItem();
    } catch (err: any) {
      console.error('❌ Save URL error:', err);
      alert(`Failed to save image URL: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUrlChange = (url: string) => {
    setImageUrl(url);
    setImagePreview(url || null);
    setImageFile(null); // Clear file if switching to URL
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item) return;
    
    try {
      setSaving(true);
      
      // Update item data using PATCH method
      await api.patch(`/items/${item.id}`, {
        sku: item.sku,
        name: item.name,
        category: item.category,
        brand: item.brand || null,
        cost_price: Number(item.cost_price),
        sell_price: Number(item.sell_price),
        unit: item.unit,
        description: item.description || null,
        barcode: item.barcode || null,
        has_warranty: item.has_warranty,
        min_stock: Number(item.min_stock),
        is_active: item.is_active,
      });
      
      // Upload image if new one selected
      if (imageFile) {
        await uploadImage();
      }
      
      dispatch(fetchItemsAsync() as any);
      alert('✅ Item updated successfully!');
      navigate('/inventory');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  if (error && !item) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
        <button
          onClick={() => navigate('/inventory')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          ← Back to Inventory
        </button>
      </div>
    );
  }

  if (!item) return null;

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/inventory')}
            className="text-gray-600 hover:text-gray-800"
            title="Back to Inventory"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Edit Item</h1>
            <p className="text-sm text-gray-500 mt-1">Update product information and image</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload Section */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">📷 Product Image</h2>
            
            {/* Mode Switcher */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setImageUploadMode('file')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  imageUploadMode === 'file'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📤 Upload File
              </button>
              <button
                type="button"
                onClick={() => setImageUploadMode('url')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  imageUploadMode === 'url'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🔗 External URL
              </button>
            </div>
            
            <div className="flex gap-4">
              {/* Image Preview */}
              <div className="w-64 h-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-full max-h-full object-contain"
                    onError={() => setImagePreview(null)}
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p>No image</p>
                  </div>
                )}
              </div>

              {/* Upload Controls - File Mode */}
              {imageUploadMode === 'file' && (
                <div className="flex-1 space-y-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  className="hidden"
                />
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload Image
                </button>

                <button
                  type="button"
                  onClick={showCamera ? capturePhoto : startCamera}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {showCamera ? 'Capture Photo' : 'Take Photo'}
                </button>

                {imageFile && (
                  <button
                    type="button"
                    onClick={uploadImage}
                    disabled={uploadingImage}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                  >
                    {uploadingImage ? 'Uploading...' : '💾 Save Image Only'}
                  </button>
                )}

                {item.image?.has_custom && (
                  <div className="text-sm text-green-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Custom image uploaded
                  </div>
                )}
              </div>
              )}

              {/* URL Input Mode */}
              {imageUploadMode === 'url' && (
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image URL
                    </label>
                    <input
                      type="url"
                      value={imageUrl}
                      onChange={(e) => handleImageUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={saveImageUrl}
                    disabled={uploadingImage || !imageUrl.trim()}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                  >
                    {uploadingImage ? 'Saving...' : '💾 Save Image URL'}
                  </button>

                  {item.image?.type === 'url' && (
                    <div className="text-sm text-green-600 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      External URL saved
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Camera View */}
            {showCamera && (
              <div className="mt-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full max-w-md mx-auto border-2 border-gray-300 rounded-lg"
                />
                <button
                  type="button"
                  onClick={stopCamera}
                  className="mt-2 text-red-600 hover:text-red-800 text-sm"
                >
                  Cancel Camera
                </button>
              </div>
            )}
            
            <canvas ref={canvasRef} className="hidden" />
          </div>

          {/* Basic Info */}
          <div className="bg-white p-6 rounded-lg shadow space-y-4">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={item.sku}
                  onChange={(e) => setItem({ ...item, sku: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => setItem({ ...item, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={item.name}
                onChange={(e) => setItem({ ...item, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={item.cost_price}
                  onChange={(e) => setItem({ ...item, cost_price: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sell Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={item.sell_price}
                  onChange={(e) => setItem({ ...item, sell_price: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <input
                  type="text"
                  value={item.unit}
                  onChange={(e) => setItem({ ...item, unit: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand
                </label>
                <input
                  type="text"
                  value={item.brand || ''}
                  onChange={(e) => setItem({ ...item, brand: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Barcode
                </label>
                <input
                  type="text"
                  value={item.barcode || ''}
                  onChange={(e) => setItem({ ...item, barcode: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={item.description || ''}
                onChange={(e) => setItem({ ...item, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Stock
                </label>
                <input
                  type="number"
                  value={item.min_stock}
                  onChange={(e) => setItem({ ...item, min_stock: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  min="0"
                />
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.has_warranty}
                    onChange={(e) => setItem({ ...item, has_warranty: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Has Warranty</span>
                </label>
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.is_active}
                    onChange={(e) => setItem({ ...item, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/inventory')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg"
            >
              {saving ? 'Saving...' : 'Update Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditItemPage;
